/**
 * Market display utilities — pure formatters and timezone helpers.
 *
 * Note: market sentiment / spread CLASSIFICATION is computed by the backend
 * (see GET /api/market/context). The frontend only renders the result; we
 * do NOT recompute weights or thresholds client-side. If backend is down,
 * UI shows "loading"/"error" state, never a silent fallback.
 */

// ===== ET (Eastern Time) Utilities =====

export const ET_TIMEZONE = 'America/New_York';

export const getETNow = () => {
  const etString = new Date().toLocaleString('en-US', { timeZone: ET_TIMEZONE });
  return new Date(etString);
};

export const getETDateString = () => {
  const et = getETNow();
  return `${et.getFullYear()}-${String(et.getMonth() + 1).padStart(2, '0')}-${String(et.getDate()).padStart(2, '0')}`;
};

export const getETTimeId = () => {
  const et = getETNow();
  return `${String(et.getHours()).padStart(2, '0')}${String(et.getMinutes()).padStart(2, '0')}${String(et.getSeconds()).padStart(2, '0')}`;
};

export const getETDateTimeString = () => {
  const et = getETNow();
  const date = getETDateString();
  const time = `${String(et.getHours()).padStart(2, '0')}:${String(et.getMinutes()).padStart(2, '0')}:${String(et.getSeconds()).padStart(2, '0')}`;
  return `${date}T${time}`;
};

// ===== Sentiment Display Helpers =====
// Sentiment values come from backend; these helpers map to visuals.

/**
 * Format sentiment label with backend-computed trend score.
 * Use null for trendScore when not available (loading/error state).
 */
export const formatMarketSentiment = (trendScore, sentiment) => {
  const sentimentName = sentiment === 'loading' ? 'Loading'
    : sentiment === 'error' ? 'Error'
    : sentiment ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1)
    : 'Unknown';
  if (trendScore == null) return sentimentName;
  // Label-first format ("Bearish -0.51%") matches the indices row pattern
  // ("S&P 500 -0.41%") so the eye scans identifier→value uniformly across Row 2.
  return `${sentimentName} ${trendScore >= 0 ? '+' : ''}${trendScore.toFixed(2)}%`;
};

export const getSentimentEmoji = (sentiment) => {
  switch (sentiment) {
    case 'bullish': return '🟢';
    case 'mixed':   return '🟡';
    case 'bearish': return '🔴';
    case 'error':   return '⚠️';
    default:        return '⏳';
  }
};

export const getSentimentColor = (sentiment) => {
  switch (sentiment) {
    case 'bullish': return '#16a34a';
    case 'mixed':   return '#ca8a04';
    case 'bearish': return '#dc2626';
    case 'error':   return '#dc2626';
    default:        return '#6b7280';
  }
};

// ===== Spread Class Display Helpers =====
// IWM-SPY spread classification from backend; maps to visuals.

export const getSpreadEmoji = (spreadClass) => {
  switch (spreadClass) {
    case 'severe-narrow':     return '🚨';
    case 'mild-narrow':       return '⚠️';
    case 'small-cap-leading': return '📈';
    case 'balanced':          return '📊';
    default:                  return '📊';
  }
};

export const getSpreadColor = (spreadClass) => {
  switch (spreadClass) {
    case 'severe-narrow':
      return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
    case 'mild-narrow':
      return { bg: '#fefce8', border: '#fef08a', text: '#ca8a04' };
    case 'small-cap-leading':
      return { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' };
    case 'balanced':
      return { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280' };
    default:
      return { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280' };
  }
};

// ===== Day mode (handbook rule: the day's mode is fixed by the FIRST auto scan) =====
// Tier thresholds follow the handbook (2.8): bullish "strong open" at trend >= +0.90
// (normal between +0.30 and +0.90); bearish "severe" at trend <= -0.60 (mild between
// -0.30 and -0.60). Mixed is never split. The backend still decides the regime label;
// the tier is a display refinement on the backend's trend score.
export const REGIME_TIERS = { bullishStrong: 0.90, bearishSevere: -0.60 };

export const getRegimeTier = (trendScore, sentiment) => {
  if (trendScore == null) return null;
  if (sentiment === 'bullish') return trendScore >= REGIME_TIERS.bullishStrong ? 'strong' : 'normal';
  if (sentiment === 'bearish') return trendScore <= REGIME_TIERS.bearishSevere ? 'severe' : 'mild';
  return null;
};

/** "Bearish severe -0.74%" / "Bullish normal +0.36%" / "Mixed +0.14%". */
export const formatDayMode = (trendScore, sentiment) => {
  const name = sentiment ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1) : 'Unknown';
  const tier = getRegimeTier(trendScore, sentiment);
  const score = trendScore == null ? '' : ` ${trendScore >= 0 ? '+' : ''}${trendScore.toFixed(2)}%`;
  return `${name}${tier ? ` ${tier}` : ''}${score}`;
};

/** Rows from GET /api/scan/list -> [{id, timestamp, source, data, regime, pickCount}] (rows whose data fails to parse are dropped). */
export const parseScanRows = (rows) => (Array.isArray(rows) ? rows : []).map((r) => {
  let data = null;
  try {
    data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
  } catch (e) {
    data = null;
  }
  return {
    id: r.id,
    timestamp: r.timestamp, // ET wall-clock time from the backend (no zone suffix)
    source: r.source,
    data,
    regime: data?.marketContext?.marketSentiment || null,
    pickCount: Array.isArray(data?.allRecommendedStocks) ? data.allRecommendedStocks.length : null,
  };
}).filter((r) => r.data);

export const DAY_MODE_NONE_AFTER_MINUTES = 10 * 60 + 10; // 10:10 ET: the 09:45 scan is in by then or not at all

/**
 * Day mode from the day's parsed scan rows: the earliest source=auto scan fixes it.
 * {status:'set', id, date, time:'HH:MM', sentiment, trendScore} | {status:'waiting'} | {status:'none'}.
 */
export const resolveDayMode = (scans, etNow) => {
  const autos = (scans || []).filter((s) => s.source === 'auto' && s.data?.marketContext);
  if (autos.length) {
    const first = autos.slice().sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)))[0];
    const mc = first.data.marketContext;
    const ts = String(first.timestamp);
    return { status: 'set', id: first.id, date: ts.slice(0, 10), time: ts.slice(11, 16), sentiment: mc.marketSentiment || null, trendScore: mc.trendScore ?? null };
  }
  const minutes = etNow.getHours() * 60 + etNow.getMinutes();
  return { status: minutes >= DAY_MODE_NONE_AFTER_MINUTES ? 'none' : 'waiting' };
};

export const formatDayModeLabel = (dayMode) => {
  if (!dayMode) return 'Day mode · waiting for the 09:45 scan';
  switch (dayMode.status) {
    case 'set': return `Day mode ${dayMode.time} · ${formatDayMode(dayMode.trendScore, dayMode.sentiment)}`;
    case 'none': return 'Day mode · no auto scan today';
    case 'error': return 'Day mode · unavailable';
    default: return 'Day mode · waiting for the 09:45 scan';
  }
};

/** Pill colors for the Day mode chip: filled by regime, grey while waiting / none / error. */
export const getDayModeStyle = (dayMode) => {
  const s = dayMode?.status === 'set' ? dayMode.sentiment : null;
  const bg = s === 'bullish' ? '#16a34a' : s === 'mixed' ? '#ca8a04' : s === 'bearish' ? '#dc2626' : '#e5e7eb';
  return { backgroundColor: bg, color: s ? '#ffffff' : '#374151' };
};

/**
 * Stored-scan snapshots -> stockData rows for the AI panel (VWAP / RVOL / Gap cells). picksSnapshot and
 * universeSnapshot are dicts keyed by symbol ({currentPrice, vwap, relativeVolume, gapPercent, ...}); picks win
 * on overlap. Legacy array form ([{symbol, ...}]) is accepted too. Returns null when the scan has no snapshot.
 */
export const snapshotToStockRows = (scanData) => {
  const toEntries = (node) => {
    if (Array.isArray(node)) return node.filter((v) => v && v.symbol).map((v) => [v.symbol, v]);
    if (node && typeof node === 'object') return Object.entries(node);
    return [];
  };
  const merged = {};
  [...toEntries(scanData?.universeSnapshot), ...toEntries(scanData?.picksSnapshot)].forEach(([symbol, v]) => {
    if (symbol && v && typeof v === 'object') merged[symbol] = { ...(merged[symbol] || {}), ...v };
  });
  const rows = Object.entries(merged).map(([symbol, v]) => ({ symbol, ...v, price: v.currentPrice ?? v.price ?? null }));
  return rows.length ? rows : null;
};

